import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import CustomButton from 'components/CustomButton/CustomButton';
import { useAnalysisTracking } from '../../../../hooks/useAnalytics';
import { withAnalysisTracking } from '../../../../components/Statistics/AnalysisTracker';

const InteractionsMedicamenteusesForm = () => {
    const [medPrescrits, setMedPrescrits] = useState([]);
    const [medAAnalyser, setMedAAnalyser] = useState([]);
    const [medsOptions, setMedsOptions] = useState([]);
    const debounceRef = useRef(null);
    const [resultats, setResultats] = useState([]);
    const [alternatives, setAlternatives] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorText, setErrorText] = useState("");
    const [activeTab, setActiveTab] = useState('results');
    const [selectedMedForAlternatives, setSelectedMedForAlternatives] = useState(null);
    const API_ROOT = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1';
    
    // Get analysis tracking functions from the hook
    const { trackAnalysis, saveAnalysis, markAsConsidered, startAnalysis } = useAnalysisTracking();

    useEffect(() => {
        const loadInitialOptions = async () => {
            try {
                const mongoResponse = await fetch(`${API_ROOT}/mongo/products?limit=1000&sort=nom`);
                if (mongoResponse.ok) {
                    const mongoData = await mongoResponse.json();
                    let initialOptions = (mongoData.products || mongoData.data || mongoData || [])
                        .filter(med => med && med.nom && med.nom.trim())
                        .map(formatMedicament);

                    if (initialOptions.length < 500) {
                        try {
                            const additionalResponse = await fetch(`${API_ROOT}/mongo/products?skip=1000&limit=500&sort=nom`);
                            if (additionalResponse.ok) {
                                const additionalData = await additionalResponse.json();
                                const additionalOptions = (additionalData.products || additionalData.data || additionalData || [])
                                    .filter(med => med && med.nom && med.nom.trim())
                                    .map(formatMedicament);
                                additionalOptions.forEach(med => {
                                    if (!initialOptions.find(option => option.value === med.value)) {
                                        initialOptions.push(med);
                                    }
                                });
                            }
                        } catch (e) {}
                    }

                    initialOptions.sort((a, b) => a.label.localeCompare(b.label));
                    setMedsOptions(initialOptions);
                }
            } catch (error) {
                setMedsOptions([]);
            }
        };

        loadInitialOptions();
    }, []);

    const fetchOptions = async (term) => {
        try {
            setIsLoading(true);
            let allOptions = [];

            if (!term || term.trim().length < 1) {
                setIsLoading(false);
                return;
            }

            try {
                const mongoUrl = `${API_ROOT}/mongo/products?nom=${encodeURIComponent(term.trim())}&limit=300`;
                const mongoResponse = await fetch(mongoUrl);

                if (mongoResponse.ok) {
                    const mongoData = await mongoResponse.json();
                    let medicaments = mongoData;

                    if (Array.isArray(mongoData)) {
                        medicaments = mongoData;
                    } else if (mongoData.products) {
                        medicaments = mongoData.products;
                    } else if (mongoData.data) {
                        medicaments = mongoData.data;
                    } else if (mongoData.results) {
                        medicaments = mongoData.results;
                    }

                    const mongoOptions = medicaments
                        .filter(med => med && med.nom && med.nom.trim())
                        .map(formatMedicament);
                    allOptions = [...mongoOptions];
                }

                if (allOptions.length < 10 && term.trim().length >= 3) {
                    try {
                        const broadSearchUrl = `${API_ROOT}/mongo/products?search=${encodeURIComponent(term.trim())}&limit=200`;
                        const broadResponse = await fetch(broadSearchUrl);

                        if (broadResponse.ok) {
                            const broadData = await broadResponse.json();
                            const broadResults = (broadData.products || broadData.data || broadData || [])
                                .filter(med => med && med.nom && med.nom.trim())
                                .map(formatMedicament);

                            broadResults.forEach(med => {
                                if (!allOptions.find(option => option.value === med.value)) {
                                    allOptions.push(med);
                                }
                            });
                        }
                    } catch (broadSearchError) {}
                }
            } catch (error) {}

            const uniqueOptions = Array.from(
                new Map(allOptions.map(item => [item.value, item])).values()
            );

            const sortedOptions = uniqueOptions.sort((a, b) => {
                const aStartsWith = a.label.toLowerCase().startsWith(term.toLowerCase());
                const bStartsWith = b.label.toLowerCase().startsWith(term.toLowerCase());
                if (aStartsWith && !bStartsWith) return -1;
                if (!aStartsWith && bStartsWith) return 1;
                return a.label.localeCompare(b.label);
            });

            setMedsOptions(sortedOptions);
        } catch (error) {
            setMedsOptions([]);
        } finally {
            setIsLoading(false);
        }
    };

    const formatMedicament = (med) => {
        try {
            const rawName = (med.nom || med.name || '').trim();
            const name = rawName.toUpperCase();
            const dci = med.dci || med.denomination || med.DCI || '';
            const famille = med.famille || med.familleTherapeutique || '';
            const classe = med.classe || med.classePharmacologique || '';
            const profilsContreIndiques = med.profilsContreIndiques || med.profilesContreIndiquees || '';

            return {
                value: name,
                label: name,
                data: {
                    nom: rawName,
                    dci,
                    famille,
                    classe,
                    profilsContreIndiques,
                    indication: med.indication || '',
                    forme: med.forme || '',
                    dosage: med.dosage || '',
                    laboratoire: med.laboratoire || '',
                    interactionsContreIndiquees: med.interactionsContreIndiquees || '',
                    interactionsDeconseillees: med.interactionsDeconseillees || '',
                    interactionsPrecaution: med.interactionsPrecaution || '',
                    DCI: dci,
                    familleTherapeutique: famille,
                    classePharmacologique: classe,
                    ...med
                },
                rawName
            };
        } catch (error) {
            return {
                value: 'ERREUR',
                label: 'ERREUR - Données invalides',
                data: {},
                rawName: ''
            };
        }
    };

    const handleInputChange = (val) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            if (val && val.trim().length >= 0) fetchOptions(val);
        }, 200);
        return val;
    };

    // ─── Analyse d'interaction entre deux médicaments ───────────────────────────
    const analyserInteractionsCompletes = (med1, med2) => {
        if (!med1 || !med2) return { niveau: 'aucun', raison: '', compatible: true };

        const data1 = med1.data || {};
        const data2 = med2.data || {};
        const nom1 = (data1.nom || '').toLowerCase().trim();
        const nom2 = (data2.nom || '').toLowerCase().trim();

        if (nom1 === nom2) return { niveau: 'compatible', raison: 'Même médicament', compatible: true, gravite: 'aucune' };

        // Vérifier contre-indications explicites
        const matchesCIExplicite = (interactionsStr, nomCible) => {
            if (!interactionsStr || !nomCible || nomCible.length < 4) return false;
            const items = interactionsStr.toLowerCase().split(/[;,|$]/).map(s => s.trim()).filter(s => s.length > 3);
            const regex = new RegExp(`\\b${nomCible.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
            return items.some(item => item === nomCible || regex.test(item));
        };

        const ciList1 = data1.interactionsContreIndiquees || '';
        const ciList2 = data2.interactionsContreIndiquees || '';
        const nomCI1 = data1.nomCommercialContreIndique || '';
        const nomCI2 = data2.nomCommercialContreIndique || '';

        if (
            matchesCIExplicite(ciList1, nom2) ||
            matchesCIExplicite(ciList2, nom1) ||
            matchesCIExplicite(nomCI1, nom2) ||
            matchesCIExplicite(nomCI2, nom1)
        ) {
            return { niveau: 'contre-indique', raison: 'Interaction contre-indiquée documentée', compatible: false, gravite: 'haute' };
        }

        // Vérifier associations déconseillées
        const matchesKeyword = (str, nom) => {
            if (!str || !nom || nom.length < 4) return false;
            const regex = new RegExp(`\\b${nom.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
            return regex.test(str);
        };

        if (matchesKeyword(data1.interactionsDeconseillees || '', nom2) || matchesKeyword(data2.interactionsDeconseillees || '', nom1)) {
            return { niveau: 'deconseille', raison: 'Association déconseillée - Surveiller étroitement', compatible: true, gravite: 'moyenne' };
        }

        if (matchesKeyword(data1.interactionsPrecaution || '', nom2) || matchesKeyword(data2.interactionsPrecaution || '', nom1)) {
            return { niveau: 'precaution', raison: 'Association possible avec précautions - Adapter les doses si nécessaire', compatible: true, gravite: 'faible' };
        }

        const dci1 = (data1.DCI || data1.dci || '').toLowerCase().trim();
        const dci2 = (data2.DCI || data2.dci || '').toLowerCase().trim();
        if (dci1 && dci2 && dci1 === dci2 && dci1.length > 3) {
            return { niveau: 'attention-dosage', raison: `Substance active identique (${dci1}) - Vérifier le dosage total pour éviter le surdosage`, compatible: true, gravite: 'faible' };
        }

        return { niveau: 'compatible', raison: 'Aucune interaction connue', compatible: true, gravite: 'aucune' };
    };

    // ─── Fonction pour vérifier si un médicament correspond aux critères ─────────

    // ─── Calcul du score de pertinence ─────────────────────────────────────────
    const calculateAlternativeScore = (medication, searchCriteria) => {
        let score = 0;

        // Famille exacte : +50 points
        if (searchCriteria.famille &&
            (medication.famille === searchCriteria.famille ||
                medication.familleTherapeutique === searchCriteria.famille)) {
            score += 50;
        }
        // Famille similaire : +30 points
        else if (searchCriteria.famille) {
            const searchFamille = searchCriteria.famille.toLowerCase();
            const medFamille = (medication.famille || '').toLowerCase();
            const medFamilleTherapeutique = (medication.familleTherapeutique || '').toLowerCase();

            if (medFamille.includes(searchFamille) || searchFamille.includes(medFamille) ||
                medFamilleTherapeutique.includes(searchFamille) || searchFamille.includes(medFamilleTherapeutique)) {
                score += 30;
            }
        }

        // Classe exacte : +30 points
        if (searchCriteria.classe &&
            (medication.classe === searchCriteria.classe ||
                medication.classePharmacologique === searchCriteria.classe)) {
            score += 30;
        }
        // Classe similaire : +15 points
        else if (searchCriteria.classe) {
            const searchClasse = searchCriteria.classe.toLowerCase();
            const medClasse = (medication.classe || '').toLowerCase();
            const medClassePharmacologique = (medication.classePharmacologique || '').toLowerCase();

            if (medClasse.includes(searchClasse) || searchClasse.includes(medClasse) ||
                medClassePharmacologique.includes(searchClasse) || searchClasse.includes(medClassePharmacologique)) {
                score += 15;
            }
        }

        // Indication similaire : +20 points
        if (searchCriteria.indication && medication.indication) {
            const searchIndication = searchCriteria.indication.toLowerCase();
            const medIndication = medication.indication.toLowerCase();

            if (medIndication === searchIndication) {
                score += 20;
            } else if (medIndication.includes(searchIndication) || searchIndication.includes(medIndication)) {
                score += 10;
            }
        }

        // Bonus pour forme pharmaceutique : +5 points
        if (medication.forme) score += 5;

        // Bonus pour dosage similaire : +5 points
        if (medication.dosage) score += 5;

        return score;
    };

    const genererAlternatives = async (medicamentContreIndique, medicamentsPrescrits = []) => {
        if (!medicamentContreIndique || !medicamentContreIndique.data) return [];

        const medData = medicamentContreIndique.data;

        // ÉTAPE 2 & 3 : Identifier la famille et classe du médicament à changer
        const familleAChanger = medData.famille || medData.familleTherapeutique;
        const classeAChanger = medData.classe || medData.classePharmacologique;

        // Récupérer TOUS les médicaments disponibles
        let tousMeds = medsOptions.map(opt => ({
            value: opt.value,
            label: opt.label,
            nom: opt.data?.nom || opt.value,
            dci: opt.data?.DCI || opt.data?.dci,
            substance: opt.data?.DCI || opt.data?.dci,
            famille: opt.data?.famille || opt.data?.familleTherapeutique,
            familleTherapeutique: opt.data?.familleTherapeutique || opt.data?.famille,
            classe: opt.data?.classe || opt.data?.classePharmacologique,
            classePharmacologique: opt.data?.classePharmacologique || opt.data?.classe,
            indication: opt.data?.indication,
            forme: opt.data?.forme,
            dosage: opt.data?.dosage,
            laboratoire: opt.data?.laboratoire,
            data: opt.data
        }));

        // Si la famille est définie, charger les autres médicaments de cette famille depuis l'API
        if (familleAChanger) {
            try {
                const familyResponse = await fetch(
                    `${API_ROOT}/mongo/products?famille=${encodeURIComponent(familleAChanger)}&limit=100`
                );

                if (familyResponse.ok) {
                    const familyData = await familyResponse.json();
                    let familyMeds = familyData.products || familyData.data || familyData || [];

                    if (Array.isArray(familyMeds)) {
                        familyMeds.forEach(med => {
                            const medNom = (med.nom || '').toLowerCase().trim();
                            if (!tousMeds.find(m => m.nom.toLowerCase().trim() === medNom)) {
                                tousMeds.push({
                                    value: medNom.toUpperCase(),
                                    label: med.nom,
                                    nom: med.nom,
                                    dci: med.DCI || med.dci || '',
                                    substance: med.DCI || med.dci || '',
                                    famille: med.famille || med.familleTherapeutique || '',
                                    familleTherapeutique: med.familleTherapeutique || med.famille || '',
                                    classe: med.classe || med.classePharmacologique || '',
                                    classePharmacologique: med.classePharmacologique || med.classe || '',
                                    indication: med.indication || '',
                                    forme: med.forme || '',
                                    dosage: med.dosage || '',
                                    laboratoire: med.laboratoire || '',
                                    data: med
                                });
                            }
                        });
                    }
                }
            } catch (error) {}
        }

        const alternatives = [];
        let countFamilleMatch = 0;

        for (const medication of tousMeds) {
            // Éviter le médicament lui-même
            if (medication.nom === medData.nom ||
                medication.value === medicamentContreIndique.value) {
                continue;
            }

            // ÉTAPE 4 : FILTRE STRICT - UNIQUEMENT MÊME FAMILLE THÉRAPEUTIQUE
            const familleNormalisee = familleAChanger ? familleAChanger.toLowerCase().trim() : '';
            const medFamilleNormalisee = medication.famille ? medication.famille.toLowerCase().trim() : '';
            const medFamilleTherapNormalisee = medication.familleTherapeutique ? medication.familleTherapeutique.toLowerCase().trim() : '';

            const sameFamily = familleNormalisee && (
                medFamilleNormalisee === familleNormalisee ||
                medFamilleTherapNormalisee === familleNormalisee ||
                medFamilleNormalisee.includes(familleNormalisee) ||
                familleNormalisee.includes(medFamilleNormalisee)
            );

            if (!sameFamily) {
                continue;
            }

            countFamilleMatch++;

            // ÉTAPE 4 (suite) : Vérifier la compatibilité avec CHAQUE médicament à maintenir
            // On EXCLUT les alternatives qui sont contre-indiquées avec les médicaments prescrits
            let avertissements = [];
            let severiteMax = 'aucun';
            let estContreIndique = false;

            for (const prescrit of medicamentsPrescrits) {
                const analyse = analyserInteractionsCompletes(prescrit, {
                    value: medication.value,
                    data: medication.data || medication
                });

                if (analyse.niveau === 'contre-indique') {
                    avertissements.push({
                        niveau: 'contre-indique',
                        medicament: prescrit.data?.nom || prescrit.value,
                        raison: analyse.raison
                    });
                    severiteMax = 'contre-indique';
                    estContreIndique = true;
                } else if (analyse.niveau === 'deconseille') {
                    avertissements.push({
                        niveau: 'deconseille',
                        medicament: prescrit.data?.nom || prescrit.value,
                        raison: analyse.raison
                    });
                    if (severiteMax !== 'contre-indique') severiteMax = 'deconseille';
                } else if (analyse.niveau === 'precaution') {
                    avertissements.push({
                        niveau: 'precaution',
                        medicament: prescrit.data?.nom || prescrit.value,
                        raison: analyse.raison
                    });
                    if (severiteMax === 'aucun') severiteMax = 'precaution';
                }
            }

            // FILTRE CRITIQUE : Rejeter les alternatives contre-indiquées avec les médicaments à maintenir
            if (estContreIndique) {
                continue;
            }

            // ÉTAPE 5 : Lister les médicaments compatibles
            const matchScore = calculateAlternativeScore(medication, {
                famille: familleAChanger,
                classe: classeAChanger,
                indication: medData.indication
            });

            alternatives.push({
                nom: medication.nom,
                dci: medication.dci || medication.substance || 'Non spécifiée',
                famille: medication.famille,
                classe: medication.classe,
                indication: medication.indication,
                forme: medication.forme,
                dosage: medication.dosage,
                laboratoire: medication.laboratoire,
                avertissements,
                severiteMax: severiteMax,
                type: 'meme_famille_exacte',
                matchScore: matchScore
            });

            // Limiter le nombre d'alternatives
            if (alternatives.length >= 15) break;
        }

        // Trier par score de pertinence
        alternatives.sort((a, b) => b.matchScore - a.matchScore);

        return alternatives.slice(0, 10);
    };

    // ─── Analyse principale ───────────────────────────────────────────────────────
     const analyserInteractions = async () => {
         if (medPrescrits.length === 0 && medAAnalyser.length === 0) {
             setErrorText("Veuillez sélectionner au moins un médicament");
             return;
         }
 
         setIsLoading(true);
         setErrorText("");
         setResultats([]);
         setAlternatives([]);
         setActiveTab('results');
 
         try {
             const resultatsAnalyse = [];
             const medicamentsContreIndiquesSet = new Set();
             let interactionCount = 0;
             let contreIndicationCount = 0;
 
             medAAnalyser.forEach(medEnvisage => {
                 medPrescrits.forEach(medPrescrit => {
                     interactionCount++;
                     const analyse = analyserInteractionsCompletes(medPrescrit, medEnvisage);
 
                     resultatsAnalyse.push({
                         med1: {
                             nom: medPrescrit.data?.nom || medPrescrit.value,
                             dci: medPrescrit.data?.DCI || medPrescrit.data?.dci || 'Non spécifiée'
                         },
                         med2: {
                             nom: medEnvisage.data?.nom || medEnvisage.value,
                             dci: medEnvisage.data?.DCI || medEnvisage.data?.dci || 'Non spécifiée'
                         },
                         compatible: analyse.compatible,
                         raison: analyse.raison,
                         niveau: analyse.niveau,
                         gravite: analyse.gravite
                     });
 
                     if (analyse.niveau === 'contre-indique') {
                         contreIndicationCount++;
                         medicamentsContreIndiquesSet.add(medEnvisage);
                     }
                 });
             });
 
             setResultats(resultatsAnalyse);
 
             // Générer les alternatives pour chaque médicament contre-indiqué
             const alternativesGenerees = [];
             for (const medCI of medicamentsContreIndiquesSet) {
                 const alts = await genererAlternatives(medCI, medPrescrits);
                 alternativesGenerees.push({
                     medicamentContreIndique: {
                         nom: medCI.data?.nom || medCI.value,
                         dci: medCI.data?.DCI || medCI.data?.dci || 'Non spécifiée',
                         famille: medCI.data?.famille || medCI.data?.familleTherapeutique || '',
                         classe: medCI.data?.classe || medCI.data?.classePharmacologique || ''
                     },
                     raison: resultatsAnalyse.find(r => r.med2?.nom === (medCI.data?.nom || medCI.value) && !r.compatible)?.raison || 'Contre-indication détectée',
                     alternatives: alts
                 });
             }
 
             setAlternatives(alternativesGenerees);
 
                 // Track the analysis for statistics
             try {
                 // Get user from localStorage or Firebase
                 let userId = localStorage.getItem('userId') || localStorage.getItem('authUserId') || 'anonymous';
                 if (userId === 'anonymous') {
                     // Try to get from Firebase auth if available
                     try {
                         // This would require importing auth, but we'll keep it simple for now
                         const lastUser = localStorage.getItem('lastLoggedUser');
                         if (lastUser) {
                             const userData = JSON.parse(lastUser);
                             userId = userData.email || userId;
                         }
                     } catch (e) {}
                 }
                 
                 if (userId === 'anonymous') {
                     userId = 'user_' + Date.now();
                 }
 
                 // Prepare analysis data
                 const analysisData = {
                     type: 'DRUG_INTERACTION',
                     medications: [...medPrescrits.map(m => m.value), ...medAAnalyser.map(m => m.value)],
                     findingsCount: contreIndicationCount,
                     findings: [],
                     riskLevel: contreIndicationCount > 0 ? 'HIGH' : 'LOW',
                     patientContext: [], // No patient context in this form
                     responseTime: 0, // We don't measure response time here
                     cacheHit: false
                 };
 
// Track the analysis using the hook functions
                  const trackingData = {
                      analysisType: analysisData.type,
                      medications: analysisData.medications,
                      riskLevel: analysisData.riskLevel,
                      findingsCount: analysisData.findingsCount,
                      findings: analysisData.findings,
                      patientContext: analysisData.patientContext,
                      responseTime: analysisData.responseTime,
                      cacheHit: analysisData.cacheHit
                  };
                  
                  const analysisId = await trackAnalysis(trackingData);

                  if (analysisId) {
                      // Track save (considered as saved since we're showing results)
                      await saveAnalysis(analysisId, {
                          name: `Analyse d'interactions médicamenteuses`,
                          notes: `Analyse de ${medPrescrits.length} médicament(s) prescrit(s) et ${medAAnalyser.length} médicament(s) envisagé(s)`
                      });

                      // Mark as considered (consulted) immediately after analysis
                      await markAsConsidered(analysisId, {
                          timeSpentSeconds: 5, // Assume user spent at least 5 seconds viewing results
                          action: 'VIEW_DETAILS'
                      });
                  }
             } catch (trackingError) {}
 
             // Message récapitulatif
             const niveaux = resultatsAnalyse.reduce((acc, r) => {
                 acc[r.niveau] = (acc[r.niveau] || 0) + 1;
                 return acc;
             }, {});
 
             const msgs = [];
             if (niveaux['contre-indique']) msgs.push(`${niveaux['contre-indique']} contre-indication(s) GRAVE(S)`);
             if (niveaux['deconseille']) msgs.push(`${niveaux['deconseille']} association(s) déconseillée(s)`);
             if (niveaux['precaution']) msgs.push(`${niveaux['precaution']} interaction(s) nécessitant des précautions`);
             if (niveaux['attention-dosage']) msgs.push(`${niveaux['attention-dosage']} attention(s) dosage`);
 
             if (resultatsAnalyse.length === 0) {
                 setErrorText("Aucune interaction à analyser.");
             } else if (msgs.length === 0) {
                 setErrorText("");
             } else {
                 setErrorText(`${niveaux['contre-indique'] ? '🚫' : '⚠️'} ${msgs.join(', ')}`);
             }
         } catch (error) {
             setErrorText("Erreur lors de l'analyse des interactions. Veuillez réessayer.");
         } finally {
             setIsLoading(false);
         }
     };

    const clearForm = () => {
        setMedPrescrits([]);
        setMedAAnalyser([]);
        setResultats([]);
        setAlternatives([]);
        setErrorText("");
        setActiveTab('results');
        setSelectedMedForAlternatives(null);
    };

    // ─── Styles ────────────────────────────────────────────────────────────────
    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '56px',
            fontSize: '16px',
            borderRadius: '12px',
            border: state.isFocused ? '2px solid var(--app-primary-color)' : '2px solid #e2e8f0',
            boxShadow: state.isFocused ? '0 0 0 1px var(--app-primary-color)' : '0 2px 8px rgba(0,0,0,0.05)',
            '&:hover': { border: '2px solid var(--app-primary-color)' }
        }),
        valueContainer: (provided) => ({
            ...provided,
            minHeight: '52px',
            padding: '8px 12px'
        }),
        placeholder: (provided) => ({
            ...provided,
            fontSize: '16px'
        }),
        menu: (provided) => ({
            ...provided,
            fontSize: '15px',
            zIndex: 9999,
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            border: '1px solid #e2e8f0',
            marginTop: '4px'
        }),
        option: (provided, state) => ({
            ...provided,
            fontSize: '15px',
            padding: '12px 16px',
            borderRadius: '8px',
            margin: '2px',
            backgroundColor: state.isSelected ? 'var(--app-primary-color)' : state.isFocused ? 'var(--app-primary-tint)' : 'white',
            color: state.isSelected ? 'white' : '#333',
            cursor: 'pointer'
        })
    };

    const interactionsTotales = resultats.length;
    const interactionsContreIndiquees = resultats.filter(r => r.niveau === 'contre-indique').length;
    const interactionsCompatibles = resultats.filter(r => r.niveau === 'compatible').length;

    const getInteractionDisplay = (niveau) => {
        switch (niveau) {
            case 'contre-indique': return { icon: '🚫', text: 'CONTRE-INDIQUÉ', bg: 'rgba(220,53,69,0.08)', color: '#dc3545' };
            case 'deconseille':   return { icon: '⚠️', text: 'DÉCONSEILLÉ',    bg: 'rgba(255,193,7,0.12)', color: '#b8860b' };
            case 'precaution':    return { icon: '⚡', text: 'PRÉCAUTION',      bg: 'rgba(255,152,0,0.10)', color: '#e65c00' };
            case 'attention-dosage': return { icon: '📊', text: 'ATTENTION DOSAGE', bg: 'var(--app-primary-tint)', color: 'var(--app-primary-color)' };
            default:              return { icon: '✅', text: 'COMPATIBLE',      bg: 'rgba(40,167,69,0.07)', color: '#1e7e34' };
        }
    };

    return (
        <div className="imf-form-root" style={{ width: '100%', margin: '0 auto', padding: 0, fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif', color: '#334155' }}>

            {/* Formulaire */}
            <div style={{ background: '#fff', border: '1px solid #e8eef1', borderRadius: '14px', boxShadow: '0 8px 24px rgba(31, 58, 69, 0.05)', padding: '24px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '20px' }}>
                    <div style={{ flex: '1', minWidth: '300px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50', fontSize: '16px' }}>
                            Médicaments en cours d'utilisation
                        </label>
                        <Select
                            isMulti
                            options={medsOptions}
                            value={medPrescrits}
                            onChange={setMedPrescrits}
                            onInputChange={handleInputChange}
                            isLoading={isLoading}
                            noOptionsMessage={({ inputValue }) => !inputValue ? 'Commencez à taper pour rechercher' : isLoading ? 'Recherche en cours...' : `Aucun médicament trouvé pour "${inputValue}"`}
                            placeholder="Sélectionner les médicaments en cours d'utilisation..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={customSelectStyles}
                            isClearable={false}
                            isSearchable={true}
                        />
                        <div style={{ marginTop: '8px', fontSize: '14px', color: '#7f8c8d' }}>
                            {medPrescrits.length} médicament(s) sélectionné(s)
                        </div>
                    </div>

                    <div style={{ flex: '1', minWidth: '300px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#2c3e50', fontSize: '16px' }}>
                            Médicaments envisagés
                        </label>
                        <Select
                            isMulti
                            options={medsOptions}
                            value={medAAnalyser}
                            onChange={setMedAAnalyser}
                            onInputChange={handleInputChange}
                            isLoading={isLoading}
                            noOptionsMessage={({ inputValue }) => !inputValue ? 'Commencez à taper pour rechercher' : isLoading ? 'Recherche en cours...' : `Aucun médicament trouvé pour "${inputValue}"`}
                            placeholder="Sélectionner les médicaments envisagés..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={customSelectStyles}
                            isClearable={false}
                            isSearchable={true}
                        />
                        <div style={{ marginTop: '8px', fontSize: '14px', color: '#7f8c8d' }}>
                            {medAAnalyser.length} médicament(s) sélectionné(s)
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                    <CustomButton
                        className="imf-button imf-button-primary"
                        onClick={analyserInteractions}
                        disabled={isLoading || (!medPrescrits.length && !medAAnalyser.length)}
                        bsStyle="primary"
                    >
                        {isLoading ? (
                            <>
                                <span style={{ display: 'inline-block', width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white', borderRadius: '50%', marginRight: '8px', animation: 'spin 1s linear infinite' }} />
                                Analyse en cours...
                            </>
                        ) : (
                            <><span style={{ marginRight: '8px' }}>🔍</span>Analyser les interactions</>
                        )}
                    </CustomButton>

                    <button
                        type="button"
                        className="imf-button imf-button-secondary"
                        onClick={clearForm}
                        disabled={isLoading}
                    >
                        <span>🗑️</span> Effacer tout
                    </button>
                </div>
            </div>

            {/* Message résumé */}
            {errorText && (
                <div style={{
                    padding: '12px 16px', borderRadius: '6px', marginBottom: '20px',
                    display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '500',
                    ...(errorText.includes('✅') ? { background: '#d4edda', border: '1px solid #c3e6cb', color: '#155724' } : { background: '#fff3cd', border: '1px solid #ffeeba', color: '#856404' })
                }}>
                    {errorText}
                </div>
            )}

            {/* Résultats */}
            {(resultats.length > 0 || alternatives.length > 0) && (
                <div style={{ background: '#fff', border: '1px solid #e8eef1', borderRadius: '14px', boxShadow: '0 8px 24px rgba(31, 58, 69, 0.05)', overflow: 'hidden', marginTop: '20px' }}>
                    {/* Onglets */}
                    <div style={{ display: 'flex', borderBottom: '1px solid #dee2e6', background: '#f8f9fa' }}>
                        <button
                            type="button"
                            className={`imf-tab${activeTab === 'results' ? ' is-active' : ''}`}
                            onClick={() => setActiveTab('results')}
                        >
                            Interactions ({resultats.length})
                        </button>
                        <button
                            type="button"
                            className={`imf-tab${activeTab === 'alternatives' ? ' is-active' : ''}`}
                            onClick={() => alternatives.length > 0 && setActiveTab('alternatives')}
                            disabled={alternatives.length === 0}
                        >
                            Alternatives ({alternatives.length})
                        </button>
                    </div>

                    {/* Onglet Interactions */}
                    {activeTab === 'results' && (
                        <div style={{ padding: '20px' }}>
                            {interactionsTotales > 0 && (
                                <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '15px', marginBottom: '20px', display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                                    {[
                                        { label: 'Analysées', value: interactionsTotales, color: '#333' },
                                        { label: 'Compatibles', value: interactionsCompatibles, color: '#28a745' },
                                        { label: 'Contre-indiquées', value: interactionsContreIndiquees, color: '#dc3545' }
                                    ].map(({ label, value, color }) => (
                                        <div key={label} style={{ textAlign: 'center' }}>
                                            <span style={{ fontSize: '24px', fontWeight: 'bold', display: 'block', color }}>{value}</span>
                                            <span style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase' }}>{label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <h3 style={{ marginTop: 0, color: '#2c3e50', marginBottom: '20px' }}>Résultats de l&apos;analyse des interactions</h3>

                            {resultats.length > 0 ? (
                                <div style={{ overflowX: 'auto', border: '1px solid #dee2e6', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                        <tr>
                                            {['Médicament prescrit', 'Médicament envisagé', 'Interaction'].map(h => (
                                                <th key={h} style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #eef2f7 100%)', padding: '14px 18px', textAlign: 'left', fontWeight: '700', color: '#1f2937', borderBottom: '1px solid #dee2e6', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.3px' }}>
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {resultats.map((r, i) => {
                                            const disp = getInteractionDisplay(r.niveau);
                                            return (
                                                <tr key={i} style={{ backgroundColor: i % 2 === 0 ? 'white' : '#fafafa' }}>
                                                    <td style={{ padding: '14px 18px', borderBottom: '1px solid #edf2f7' }}>
                                                        <div style={{ fontWeight: '500', fontSize: '16px' }}>{r.med1?.nom}</div>
                                                        <div style={{ fontSize: '13px', color: '#6c757d', fontStyle: 'italic', marginTop: '4px' }}>DCI: {r.med1?.dci}</div>
                                                    </td>
                                                    <td style={{ padding: '14px 18px', borderBottom: '1px solid #edf2f7' }}>
                                                        <div style={{ fontWeight: '500', fontSize: '16px' }}>{r.med2?.nom}</div>
                                                        <div style={{ fontSize: '13px', color: '#6c757d', fontStyle: 'italic', marginTop: '4px' }}>DCI: {r.med2?.dci}</div>
                                                    </td>
                                                    <td style={{ padding: '14px 18px', borderBottom: '1px solid #edf2f7' }}>
                                                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: disp.bg, color: disp.color, padding: '6px 12px', borderRadius: '6px', fontWeight: '600', fontSize: '13px' }}>
                                                            <span>{disp.icon}</span>
                                                            <span>{disp.text}</span>
                                                        </div>
                                                        {r.raison && (
                                                            <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '6px', fontStyle: 'italic' }}>{r.raison}</div>
                                                        )}
                                                        {r.niveau === 'contre-indique' && alternatives.length > 0 && (
                                                            <button
                                                                style={{ marginTop: '8px', background: '#ffc107', color: '#212529', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' }}
                                                                onClick={() => { setSelectedMedForAlternatives(r.med2?.nom); setActiveTab('alternatives'); }}
                                                            >
                                                                Voir les alternatives →
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d', fontStyle: 'italic' }}>Aucune interaction trouvée</div>
                            )}
                        </div>
                    )}

                    {/* Onglet Alternatives */}
                    {activeTab === 'alternatives' && (
                        <div style={{ padding: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
                                <h3 style={{ margin: 0, color: '#2c3e50' }}>🔄 Alternatives thérapeutiques proposées</h3>
                                {selectedMedForAlternatives && (
                                    <button
                                        onClick={() => setSelectedMedForAlternatives(null)}
                                        style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', color: '#6c757d' }}
                                    >
                                        ← Voir tous
                                    </button>
                                )}
                            </div>

                            {alternatives.length > 0 ? (
                                alternatives
                                    .filter(alt => !selectedMedForAlternatives || alt.medicamentContreIndique?.nom === selectedMedForAlternatives)
                                    .map((alt, index) => (
                                        <div key={index} style={{ border: '1px solid #dee2e6', borderRadius: '10px', marginBottom: '24px', overflow: 'hidden' }}>
                                            {/* En-tête médicament contre-indiqué */}
                                            <div style={{
                                                background: 'linear-gradient(135deg, #fff3cd 0%, #ffe8a8 100%)',
                                                borderLeft: '4px solid #dc3545',
                                                padding: '16px 20px',
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                    <span style={{ fontSize: '28px' }}>🚫</span>
                                                    <div>
                                                        <div style={{ fontWeight: '700', fontSize: '17px', color: '#dc3545' }}>{alt.medicamentContreIndique?.nom}</div>
                                                        <div style={{ fontSize: '13px', color: '#dc3545', fontStyle: 'italic', marginTop: '2px' }}>DCI: {alt.medicamentContreIndique?.dci || 'Non spécifiée'}</div>
                                                        {alt.medicamentContreIndique?.famille && (
                                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                                                Famille: <strong>{alt.medicamentContreIndique.famille}</strong>
                                                            </div>
                                                        )}
                                                        {alt.medicamentContreIndique?.classe && (
                                                            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                                                                Classe: <strong>{alt.medicamentContreIndique.classe}</strong>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <span style={{ background: '#dc3545', color: 'white', padding: '5px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                                                    CONTRE-INDIQUÉ
                                                </span>
                                            </div>

                                            {/* Raison */}
                                            <div style={{ background: '#fff8f8', padding: '10px 20px', borderBottom: '1px solid #f5c6cb', borderLeft: '4px solid #dc3545', fontSize: '13px', color: '#555' }}>
                                                <strong style={{ color: '#dc3545' }}>📌 Raison :</strong> {alt.raison}
                                            </div>

                                            {/* Liste des alternatives */}
                                            <div style={{ padding: '20px', background: '#fafbfc' }}>
                                                {alt.alternatives && alt.alternatives.length > 0 ? (
                                                    <>
                                                        {/* Compteur */}
                                                        <div style={{
                                                            background: '#e8f4f8', border: '1px solid #bee5eb',
                                                            borderRadius: '8px', padding: '12px 16px',
                                                            marginBottom: '16px', color: '#0c5460',
                                                            fontSize: '14px', fontWeight: '500',
                                                            display: 'flex', alignItems: 'center', gap: '10px'
                                                        }}>
                                                            <span style={{ fontSize: '18px' }}>ℹ️</span>
                                                            <span>
                                                                {alt.alternatives.length} alternative(s) de même famille thérapeutique trouvée(s) — Vérifiez les interactions avec votre traitement
                                                            </span>
                                                        </div>

                                                        {/* Grille des alternatives */}
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '12px' }}>
                                                            {alt.alternatives.map((med, mi) => (
                                                                <div key={mi} style={{
                                                                    background: 'white',
                                                                    border: `2px solid ${med.severiteMax === 'aucun' ? '#c3e6cb' : med.severiteMax === 'precaution' ? '#ffeeba' : med.severiteMax === 'deconseille' ? '#ffc107' : '#dc3545'}`,
                                                                    borderRadius: '10px',
                                                                    padding: '16px',
                                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
                                                                }}>
                                                                    {/* Nom + badge */}
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '10px' }}>
                                                                        <div style={{ flex: 1 }}>
                                                                            <div style={{ fontWeight: '700', fontSize: '15px', color: '#2c3e50' }}>{med.nom}</div>
                                                                            {med.dci && med.dci !== 'Non spécifiée' && (
                                                                                <div style={{ fontSize: '12px', color: 'var(--app-primary-color)', fontWeight: '500', marginTop: '3px' }}>💊 DCI: {med.dci}</div>
                                                                            )}
                                                                        </div>
                                                                        <span style={{
                                                                            background: med.severiteMax === 'aucun' ? '#d4edda' :
                                                                                med.severiteMax === 'precaution' ? '#e8f4f8' :
                                                                                med.severiteMax === 'deconseille' ? '#fff3cd' : '#f8d7da',
                                                                            color: med.severiteMax === 'aucun' ? '#155724' :
                                                                                med.severiteMax === 'precaution' ? '#004085' :
                                                                                med.severiteMax === 'deconseille' ? '#856404' : '#721c24',
                                                                            padding: '4px 10px', borderRadius: '12px',
                                                                            fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap'
                                                                        }}>
                                                                            {med.severiteMax === 'aucun' ? '✓ Compatible' :
                                                                                med.severiteMax === 'precaution' ? '⚠️ Précaution' :
                                                                                med.severiteMax === 'deconseille' ? '⚠️ Déconseillé' : '🚫 Contre-indiqué'}
                                                                        </span>
                                                                    </div>

                                                                    {/* Type de correspondance */}
                                                                    {med.type && med.type !== 'autre' && (
                                                                        <div style={{
                                                                            background: '#e7f3ff',
                                                                            padding: '4px 8px',
                                                                            borderRadius: '4px',
                                                                            fontSize: '11px',
                                                                            marginBottom: '10px',
                                                                            color: '#0066cc',
                                                                            display: 'inline-block'
                                                                        }}>
                                                                            {med.type === 'meme_famille_exacte' && '📚 Même famille thérapeutique'}
                                                                            {med.type === 'meme_classe_exacte' && '🔬 Même classe pharmacologique'}
                                                                            {med.type === 'famille_similaire' && '📖 Famille similaire'}
                                                                            {med.type === 'classe_similaire' && '🧪 Classe similaire'}
                                                                            {med.type === 'indication_similaire' && '🎯 Indication similaire'}
                                                                            {med.type === 'meme_dci' && '⚕️ Même substance active'}
                                                                        </div>
                                                                    )}

                                                                    {/* Famille / Classe */}
                                                                    {(med.famille || med.classe) && (
                                                                        <div style={{ background: 'var(--app-primary-tint)', borderLeft: '3px solid var(--app-primary-color)', padding: '8px 10px', borderRadius: '4px', fontSize: '12px', marginBottom: med.avertissements?.length ? '8px' : '0' }}>
                                                                            {med.famille && <div><strong>Famille:</strong> {med.famille}</div>}
                                                                            {med.classe && <div style={{ marginTop: med.famille ? '3px' : '0' }}><strong>Classe:</strong> {med.classe}</div>}
                                                                            {med.indication && <div style={{ marginTop: '3px' }}><strong>Indication:</strong> {med.indication}</div>}
                                                                        </div>
                                                                    )}

                                                                    {/* Avertissements */}
                                                                    {med.avertissements && med.avertissements.length > 0 && (
                                                                        <div style={{ marginTop: '8px' }}>
                                                                            {/* Contre-indications */}
                                                                            {med.avertissements.some(a => a.niveau === 'contre-indique') && (
                                                                                <div style={{ background: '#f8d7da', borderLeft: '3px solid #dc3545', padding: '8px 10px', borderRadius: '4px', fontSize: '12px', marginBottom: '6px', color: '#721c24' }}>
                                                                                    <strong>🚫 CONTRE-INDIQUÉ avec :</strong>
                                                                                    <ul style={{ margin: '4px 0 0', paddingLeft: '14px' }}>
                                                                                        {med.avertissements.filter(av => av.niveau === 'contre-indique').map((av, ai) => (
                                                                                            <li key={ai} style={{ marginBottom: '2px', fontSize: '11px' }}>
                                                                                                <strong>{av.medicament}</strong> — {av.raison}
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                </div>
                                                                            )}

                                                                            {/* Associations déconseillées */}
                                                                            {med.avertissements.some(a => a.niveau === 'deconseille') && (
                                                                                <div style={{ background: '#fff3cd', borderLeft: '3px solid #ffc107', padding: '8px 10px', borderRadius: '4px', fontSize: '12px', marginBottom: '6px', color: '#856404' }}>
                                                                                    <strong>⚠️ DÉCONSEILLÉ avec :</strong>
                                                                                    <ul style={{ margin: '4px 0 0', paddingLeft: '14px' }}>
                                                                                        {med.avertissements.filter(av => av.niveau === 'deconseille').map((av, ai) => (
                                                                                            <li key={ai} style={{ marginBottom: '2px', fontSize: '11px' }}>
                                                                                                <strong>{av.medicament}</strong> — {av.raison}
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                </div>
                                                                            )}

                                                                            {/* Précautions */}
                                                                            {med.avertissements.some(a => a.niveau === 'precaution') && (
                                                                                <div style={{ background: '#e8f4f8', borderLeft: '3px solid var(--app-primary-color)', padding: '8px 10px', borderRadius: '4px', fontSize: '12px', marginBottom: '6px', color: '#004085' }}>
                                                                                    <strong>⚡ PRÉCAUTION avec :</strong>
                                                                                    <ul style={{ margin: '4px 0 0', paddingLeft: '14px' }}>
                                                                                        {med.avertissements.filter(av => av.niveau === 'precaution').map((av, ai) => (
                                                                                            <li key={ai} style={{ marginBottom: '2px', fontSize: '11px' }}>
                                                                                                <strong>{av.medicament}</strong> — {av.raison}
                                                                                            </li>
                                                                                        ))}
                                                                                    </ul>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div style={{ background: '#fff3cd', border: '2px solid #ffc107', borderRadius: '10px', padding: '30px 20px', textAlign: 'center', color: '#856404' }}>
                                                        <div style={{ fontSize: '36px', marginBottom: '10px' }}>⚠️</div>
                                                        <div style={{ fontSize: '17px', fontWeight: '700', marginBottom: '8px' }}>Aucune alternative trouvée</div>
                                                        <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.5', marginBottom: '12px' }}>
                                                            Aucune alternative dans la même famille thérapeutique n'est compatible avec le traitement en cours.
                                                        </div>
                                                        <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.5', marginBottom: '12px' }}>
                                                            <strong>Une approche thérapeutique différente est nécessaire.</strong>
                                                        </div>
                                                        <div style={{ fontSize: '13px', padding: '12px', background: 'rgba(255,255,255,0.7)', borderRadius: '6px', fontStyle: 'italic', color: '#555' }}>
                                                            💡 <strong>Conseil :</strong> Consultez un pharmacien ou un médecin pour explorer des alternatives thérapeutiques adaptées à cette situation complexe.
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d', fontStyle: 'italic' }}>
                                    Aucune alternative disponible pour les médicaments contre-indiqués
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                .imf-form-root .react-select-container .react-select__control { border-radius: 12px; border: 2px solid #e2e8f0; min-height: 56px; }
                .imf-form-root .react-select-container .react-select__control--is-focused { border-color: var(--app-primary-color); box-shadow: 0 0 0 1px var(--app-primary-color); }
                .imf-form-root .react-select-container .react-select__menu { z-index: 9999 !important; border-radius: 12px; }
                .imf-form-root .react-select-container .react-select__menu-list { padding: 4px; max-height: 200px; }
                .imf-form-root .react-select-container .react-select__option { border-radius: 8px; margin: 2px; }
                .imf-form-root .react-select-container .react-select__option--is-focused { background-color: #e3f2fd; }
                .imf-form-root .react-select-container .react-select__option--is-selected { background-color: var(--app-primary-color); color: white; }
                .imf-form-root .react-select-container .react-select__multi-value { background-color: var(--app-primary-tint); border-radius: 8px; padding: 2px 6px; }
                .imf-form-root .react-select-container .react-select__multi-value__label { color: var(--app-primary-color); font-weight: 500; }
                .imf-form-root .react-select-container .react-select__multi-value__remove:hover { background-color: var(--app-primary-color); color: white; }
                .react-select__menu-portal { z-index: 9999 !important; }
                .imf-form-root .imf-button {
                    min-height: 46px;
                    padding: 11px 18px;
                    border-radius: 9px;
                    font-size: 14px;
                    font-weight: 600;
                    line-height: 1.4;
                    transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
                }
                .imf-form-root .imf-button-primary,
                .imf-form-root .imf-button-primary:hover,
                .imf-form-root .imf-button-primary:focus,
                .imf-form-root .imf-button-primary:active {
                    color: #fff !important;
                    background: var(--app-primary-color) !important;
                    border-color: var(--app-primary-color) !important;
                    box-shadow: 0 5px 14px rgba(var(--app-primary-rgb), 0.2);
                }
                .imf-form-root .imf-button-primary:hover:not(:disabled) {
                    box-shadow: 0 7px 18px rgba(var(--app-primary-rgb), 0.3);
                    transform: translateY(-1px);
                }
                .imf-form-root .imf-button-primary:disabled {
                    box-shadow: none;
                    opacity: 0.58;
                }
                .imf-form-root .imf-button-secondary {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    color: #475569;
                    background: #fff;
                    border: 1px solid #d7e1e5;
                }
                .imf-form-root .imf-button-secondary:hover:not(:disabled) {
                    color: #263746;
                    background: #f5f9fa;
                    border-color: #bdcbd1;
                }
                .imf-form-root .imf-button:focus-visible,
                .imf-form-root .imf-tab:focus-visible {
                    outline: 3px solid rgba(var(--app-primary-rgb), 0.28);
                    outline-offset: 2px;
                }
                .imf-form-root .imf-tab {
                    padding: 13px 20px;
                    color: #64748b;
                    background: transparent;
                    border: 0;
                    border-bottom: 2px solid transparent;
                    cursor: pointer;
                    font-size: 13px;
                    font-weight: 600;
                }
                .imf-form-root .imf-tab.is-active {
                    color: #087f95;
                    border-bottom-color: var(--app-primary-color);
                }
                .imf-form-root .imf-tab:disabled {
                    cursor: not-allowed;
                    opacity: 0.5;
                }
                @media (max-width: 600px) {
                    .imf-form-root .imf-button { width: 100%; }
                }
            `}</style>
        </div>
    );
};

const ConnectedForm = withAnalysisTracking(InteractionsMedicamenteusesForm);
export default ConnectedForm;
